import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useApi, Spinner, ErrBanner, today, fmtDate, fmtNum } from '../../hooks/useApi';
import { api } from '../../services/api';
import './Reconciliation.css';

export default function Reconciliation({ entryDate }) {
  const { t: tr } = useTranslation();
  const { can, user } = useAuth();
  const isAdmin = can('reconciliation', 'approve');
  // Station details for the branded report header come from the logged-in
  // user (same source the invoice PDFs use) — no backend dependency.
  const station = {
    name:    user?.station_name    || user?.name || 'Station',
    address: user?.station_address || '',
    state:   user?.station_state   || '',
    phone:   user?.phone           || '',
  };
  // Inside Daily Entry the reconciled day is fixed to the chosen entry date.
  const [date, setDate] = useState(entryDate || today());
  const { data, loading, error, reload } = useApi(() => api.reconciliation.getSummary(date), [date]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  const toggle = (s) => setExpanded(expanded === s ? null : s);

  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 5000); };

  const handleCloseDay = async () => {
    if (!data) return;
    if (Math.abs(data.difference) > 0.01) {
      notify('❌ ' + tr('reconciliation.errors.differenceMustBeZero'));
      return;
    }
    setSaving(true);
    try {
      await api.reconciliation.closeDay({ date });
      notify('✅ ' + tr('reconciliation.toasts.dayClosed'));
      reload();
    } catch (e) {
      notify('❌ ' + e.message);
    }
    setSaving(false);
  };

  const handleReset = async () => {
    if (!window.confirm(tr('reconciliation.confirmReset'))) return;
    setSaving(true);
    try {
      await api.reconciliation.reset({ date });
      notify('✅ ' + tr('reconciliation.toasts.resetSuccess'));
      reload();
    } catch (e) {
      notify('❌ ' + e.message);
    }
    setSaving(false);
  };

  const inr = (n) => '₹ ' + fmtNum(n);

  const openReport = async () => {
    setReportLoading(true);
    try {
      const r = await api.reconciliation.report(date);
      setReport(normalizeReport(r));
    } catch (e) {
      notify('❌ ' + e.message);
    }
    setReportLoading(false);
  };

  const downloadSummaryPdf = async () => {
    let r = report;
    if (!r) {
      try { r = normalizeReport(await api.reconciliation.report(date)); }
      catch (e) { notify('❌ ' + e.message); return; }
    }
    const win = window.open('', '_blank');
    if (!win) { notify('❌ ' + tr('reconciliation.errors.allowPopups')); return; }
    win.document.write(buildSummaryReportHTML(r, station, tr));
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 400);
  };

  return (
    <div className="recon-page">
      <div className="page-header">
        <h2 className="page-title">{tr('reconciliation.title')}</h2>
        <div className="date-picker-box">
          <label>{tr('reconciliation.selectDate')}</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} className="form-control" />
        </div>
      </div>

      {msg && <div className={`notify-bar ${msg.includes('✅') ? 'success' : 'error'}`}>{msg}</div>}

      {loading && <Spinner />}
      {error && <ErrBanner msg={error} />}

      {!loading && data && (
        <div className="recon-container">
          <div className="recon-grid">
            {/* Expected Cash Column */}
            <div className="recon-card expected">
              <div className="card-header">{tr('reconciliation.expectedCash')}</div>
              <div className="card-body">
                <div className={`recon-row clickable ${expanded === 'meter' ? 'expanded' : ''}`} onClick={() => toggle('meter')}>
                  <span>{tr('reconciliation.meterSales')} {expanded === 'meter' ? '▴' : '▾'}</span>
                  <span className="val-plus">{inr(data.meter_sales)}</span>
                </div>
                {expanded === 'meter' && (
                  <div className="recon-details-box">
                    <table className="details-table">
                      <thead><tr><th>{tr('common.nozzle')}</th><th>{tr('reconciliation.prod')}</th><th>{tr('common.amount')}</th></tr></thead>
                      <tbody>
                        {data.details?.meter_sales?.map((it, i) => (
                          <tr key={i}><td>{it.pump_no}-{it.nozzle_no}</td><td>{it.product_code}</td><td>{inr(it.amount)}</td></tr>
                        ))}
                        {(!data.details?.meter_sales || data.details.meter_sales.length === 0) && <tr><td colSpan="3" className="empty-text">{tr('common.noData')}</td></tr>}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className={`recon-row clickable ${expanded === 'lube' ? 'expanded' : ''}`} onClick={() => toggle('lube')}>
                  <span>{tr('reconciliation.lubeSales')} {expanded === 'lube' ? '▴' : '▾'}</span>
                  <span className="val-plus">{inr(data.lube_sales)}</span>
                </div>
                {expanded === 'lube' && (
                  <div className="recon-details-box">
                    <table className="details-table">
                      <thead><tr><th>{tr('reconciliation.bill')}</th><th>{tr('reconciliation.item')}</th><th>{tr('common.amount')}</th></tr></thead>
                      <tbody>
                        {data.details?.lube_sales?.map((it, i) => (
                          <tr key={i}><td>{it.bill_no}</td><td>{it.product_name}</td><td>{inr(it.amount)}</td></tr>
                        ))}
                        {(!data.details?.lube_sales || data.details.lube_sales.length === 0) && <tr><td colSpan="3" className="empty-text">{tr('common.noData')}</td></tr>}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className={`recon-row clickable ${expanded === 'credit' ? 'expanded' : ''}`} onClick={() => toggle('credit')}>
                  <span>{tr('reconciliation.creditSales')} {expanded === 'credit' ? '▴' : '▾'}</span>
                  <span className="val-minus">({inr(data.credit_sales)})</span>
                </div>
                {expanded === 'credit' && (
                  <div className="recon-details-box">
                    <table className="details-table">
                      <thead><tr><th>{tr('reconciliation.bill')}</th><th>{tr('common.customer')}</th><th>{tr('common.amount')}</th></tr></thead>
                      <tbody>
                        {data.details?.credit_sales?.map((it, i) => (
                          <tr key={i}><td>{it.bill_no}</td><td>{it.customer_name}</td><td>{inr(it.amount)}</td></tr>
                        ))}
                        {(!data.details?.credit_sales || data.details.credit_sales.length === 0) && <tr><td colSpan="3" className="empty-text">{tr('common.noData')}</td></tr>}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="recon-total">
                  <span>{tr('reconciliation.totalExpected')}</span>
                  <span>{inr(data.expected_cash)}</span>
                </div>
              </div>
            </div>

            {/* Actual Cash Column */}
            <div className="recon-card actual">
              <div className="card-header">{tr('reconciliation.actualCash')}</div>
              <div className="card-body">
                <div className={`recon-row clickable ${expanded === 'cash_receipts' ? 'expanded' : ''}`} onClick={() => toggle('cash_receipts')}>
                  <span>{tr('reconciliation.cashReceipts')} {expanded === 'cash_receipts' ? '▴' : '▾'}</span>
                  <span className="val-plus">{inr(data.cash_receipts)}</span>
                </div>
                {expanded === 'cash_receipts' && (
                  <div className="recon-details-box">
                    <table className="details-table">
                      <thead><tr><th>{tr('reconciliation.ref')}</th><th>{tr('reconciliation.partyDescription')}</th><th>{tr('common.amount')}</th></tr></thead>
                      <tbody>
                        {data.details?.cash_receipts?.map((it, i) => (
                          <tr key={i}>
                            <td>{it.receipt_no || <span style={{ color: '#ccc', fontStyle: 'italic' }}>--</span>}</td>
                            <td>{it.party_name}{it.description ? ` - ${it.description}` : ''}</td>
                            <td>{inr(it.amount)}</td>
                          </tr>
                        ))}
                        {(!data.details?.cash_receipts || data.details.cash_receipts.length === 0) && <tr><td colSpan="3" className="empty-text">{tr('reconciliation.noCashReceipts')}</td></tr>}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className={`recon-row clickable ${expanded === 'digital_receipts' ? 'expanded' : ''}`} onClick={() => toggle('digital_receipts')}>
                  <span>{tr('reconciliation.digitalReceipts')} {expanded === 'digital_receipts' ? '▴' : '▾'}</span>
                  <span className="val-plus">{inr(data.digital_receipts)}</span>
                </div>
                {expanded === 'digital_receipts' && (
                  <div className="recon-details-box">
                    <table className="details-table">
                      <thead><tr><th>{tr('reconciliation.ref')}</th><th>{tr('reconciliation.partyDescription')}</th><th>{tr('common.amount')}</th></tr></thead>
                      <tbody>
                        {data.details?.digital_receipts?.map((it, i) => (
                          <tr key={i}>
                            <td>{it.receipt_no || <span style={{ color: '#ccc', fontStyle: 'italic' }}>--</span>}</td>
                            <td>{it.party_name}{it.description ? ` - ${it.description}` : ''}</td>
                            <td>{inr(it.amount)}</td>
                          </tr>
                        ))}
                        {(!data.details?.digital_receipts || data.details.digital_receipts.length === 0) && <tr><td colSpan="3" className="empty-text">{tr('reconciliation.noDigitalReceipts')}</td></tr>}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className={`recon-row clickable ${expanded === 'expenses' ? 'expanded' : ''}`} onClick={() => toggle('expenses')}>
                  <span>{tr('reconciliation.totalExpenses')} {expanded === 'expenses' ? '▴' : '▾'}</span>
                  <span className="val-plus">{inr(data.total_expenses)}</span>
                </div>
                {expanded === 'expenses' && (
                  <div className="recon-details-box">
                    <table className="details-table">
                      <thead><tr><th>{tr('common.voucher')}</th><th>{tr('reconciliation.category')}</th><th>{tr('common.amount')}</th></tr></thead>
                      <tbody>
                        {data.details?.total_expenses?.map((it, i) => (
                          <tr key={i}><td>{it.voucher_no}</td><td>{it.category_name}</td><td>{inr(it.amount)}</td></tr>
                        ))}
                        {(!data.details?.total_expenses || data.details.total_expenses.length === 0) && <tr><td colSpan="3" className="empty-text">{tr('common.noData')}</td></tr>}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="recon-spacer"></div>
                <div className="recon-total">
                  <span>{tr('reconciliation.totalActual')}</span>
                  <span>{inr(data.actual_cash)}</span>
                </div>
              </div>
            </div>

          </div>

          {/* Result Banner */}
          <div className={`diff-banner ${Math.abs(data.difference) < 0.01 ? 'balanced' : 'mismatch'}`}>
            <div className="diff-label">{tr('reconciliation.difference')}</div>
            <div className="diff-value">{inr(data.difference)}</div>
            <div className="diff-status">
              {Math.abs(data.difference) < 0.01 ? tr('reconciliation.balanced') : tr('reconciliation.mismatchFound')}
            </div>
          </div>

          <div className="recon-actions">
            {data.is_closed ? (
              <div className="closure-info" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                <span className="status-badge closed">{tr('reconciliation.dayClosed')}</span>
                <p style={{ margin: 0 }}>{tr('reconciliation.closedBy')} <strong>{data.closure_info?.closed_by_name || 'Admin'}</strong> {tr('reconciliation.on')} {fmtDate(data.closure_info.closed_at)} {new Date(data.closure_info.closed_at.replace(' ', 'T'))
                  .toLocaleTimeString('en-IN', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  })}</p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
                  <button className="btn btn-secondary btn-sm" onClick={openReport} disabled={reportLoading}>
                    {reportLoading ? tr('common.loading') : tr('reconciliation.viewSummaryReport')}
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={downloadSummaryPdf}>
                    {tr('reconciliation.downloadSummaryPdf')}
                  </button>
                  {isAdmin && (
                    <button className="btn btn-primary btn-sm" onClick={handleReset} disabled={saving}>
                      {saving ? tr('reconciliation.resetting') : tr('reconciliation.resetReconciliation')}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <button
                className="btn btn-primary btn-lg"
                onClick={handleCloseDay}
                disabled={saving || Math.abs(data.difference) > 0.01}
              >
                {saving ? tr('reconciliation.processing') : tr('reconciliation.closeDay')}
              </button>
            )}
          </div>
        </div>
      )}

      {report && (
        <SummaryReportModal
          report={report}
          station={station}
          onClose={() => setReport(null)}
          onDownload={downloadSummaryPdf}
        />
      )}
    </div>
  );
}

/* ═══════════ Day End Summary Report — shared formatters ═══════════ */
const rMoney = (n) => '₹ ' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const rQty   = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 });
const rVar   = (v) => (v === null || v === undefined) ? '—' : Number(v).toLocaleString('en-IN', { maximumFractionDigits: 3 });
const rDMY   = (d) => { if (!d) return ''; const p = String(d).slice(0, 10).split('-'); return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : String(d); };
const rDMYT  = (dt) => { if (!dt) return ''; const [dp, tp] = String(dt).split(' '); return rDMY(dp) + (tp ? ' ' + tp : ''); };
const rEsc   = (s) => String(s ?? '').replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));

/* Blue branded two-column report header (left = report info, right = station),
 * matching the invoice PDF style (#1a6fad). Reused by Summary + Detailed,
 * on-screen and in the print/PDF document. Kept compact for single-A4 fit. */
function ReportHeader({ title, header, station }) {
  const { t: tr } = useTranslation();
  const h = header || {}, st = station || {};
  const wrap  = { display: 'flex', justifyContent: 'space-between', gap: 16, background: '#1a6fad', color: '#fff', padding: '12px 16px', borderRadius: 6, marginBottom: 12 };
  const lbl   = { opacity: 0.85 };
  const stateLine = [st.state ? `${tr('reconciliation.stateLabel')} ${st.state}` : '', st.phone ? `${tr('reconciliation.phoneLabel')} ${st.phone}` : ''].filter(Boolean).join(' | ');
  return (
    <div style={wrap}>
      <div style={{ fontSize: 12, lineHeight: 1.55 }}>
        <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: 0.5, marginBottom: 4 }}>{title}</div>
        <div><span style={lbl}>{tr('reconciliation.dateLabel')}</span> {rDMY(h.date)}</div>
        <div><span style={lbl}>{tr('reconciliation.shiftLabel')}</span> {h.shift || 'Full Day'}</div>
        <div><span style={lbl}>{tr('reconciliation.closedByLabel')}</span> {h.prepared_by || '—'}</div>
        <div><span style={lbl}>{tr('reconciliation.closedAtLabel')}</span> {rDMYT(h.closed_at) || '—'}</div>
      </div>
      <div style={{ fontSize: 12, lineHeight: 1.55, textAlign: 'right' }}>
        <div style={{ fontSize: 14, fontWeight: 800 }}>{st.name}</div>
        {!!st.address && <div>{st.address}</div>}
        {!!stateLine && <div>{stateLine}</div>}
      </div>
    </div>
  );
}

function buildReportHeaderHTML(title, header, station, tr) {
  const h = header || {}, st = station || {};
  const stateLine = [st.state ? `${tr('reconciliation.stateLabel')} ${rEsc(st.state)}` : '', st.phone ? `${tr('reconciliation.phoneLabel')} ${rEsc(st.phone)}` : ''].filter(Boolean).join(' | ');
  return `<div class="rpt-head">
      <div class="rpt-left">
        <div class="rpt-title">${rEsc(title)}</div>
        <div><span class="lbl">${rEsc(tr('reconciliation.dateLabel'))}</span> ${rEsc(rDMY(h.date))}</div>
        <div><span class="lbl">${rEsc(tr('reconciliation.shiftLabel'))}</span> ${rEsc(h.shift || 'Full Day')}</div>
        <div><span class="lbl">${rEsc(tr('reconciliation.closedByLabel'))}</span> ${rEsc(h.prepared_by || '—')}</div>
        <div><span class="lbl">${rEsc(tr('reconciliation.closedAtLabel'))}</span> ${rEsc(rDMYT(h.closed_at) || '—')}</div>
      </div>
      <div class="rpt-right">
        <div class="rpt-station">${rEsc(st.name || '')}</div>
        ${st.address ? `<div>${rEsc(st.address)}</div>` : ''}
        ${stateLine ? `<div>${stateLine}</div>` : ''}
      </div>
    </div>`;
}

/* The report endpoint (action=report) returns the rich report shape. If the
 * server hasn't been updated, ?action=report falls through to getSummary and
 * returns { meter_sales, cash_receipts, digital_receipts, details, ... }.
 * This normalizer accepts EITHER shape so the report still populates the
 * financial sections from getSummary's `details`. Sections that genuinely
 * aren't in getSummary (Tank Summary, Purchase Summary, fuel Qty, Variance,
 * Station Name) require the updated server. */
function normalizeReport(raw) {
  if (!raw || typeof raw !== 'object') return raw;
  if (raw.sales_by_product || raw.kpis) return raw;   // already the report shape

  const d = raw.details || {};
  const n = (v) => Number(v || 0);

  // A. Sales by product (meter details carry amount; qty only if present) + lubes
  const byProd = {};
  (d.meter_sales || []).forEach(r => {
    const p = r.product_code || '—';
    if (!byProd[p]) byProd[p] = { product: p, qty: 0, amount: 0 };
    byProd[p].amount += n(r.amount);
    byProd[p].qty    += n(r.qty);
  });
  const sales = Object.values(byProd);
  const lubeQty = (d.lube_sales || []).reduce((s, r) => s + n(r.qty), 0);
  const lubeAmt = (d.lube_sales || []).reduce((s, r) => s + n(r.amount), 0);
  if (lubeAmt || lubeQty) sales.push({ product: 'Lubes', qty: lubeQty, amount: lubeAmt });

  // C. Collections by mode (receipt details store the mode in party_name)
  const coll = { Cash: 0, UPI: 0, Card: 0, 'Bank Transfer': 0 };
  [...(d.cash_receipts || []), ...(d.digital_receipts || [])].forEach(r => {
    const m = (r.party_name || 'Cash').toString().trim() || 'Cash';
    coll[m] = (coll[m] || 0) + n(r.amount);
  });
  coll['Credit Sales'] = n(raw.credit_sales);
  const totalCollection = Object.values(coll).reduce((a, b) => a + b, 0);

  // E. Expenses by category
  const expMap = {};
  (d.total_expenses || []).forEach(r => {
    const c = (r.category_name || 'Uncategorised').toString().trim() || 'Uncategorised';
    expMap[c] = (expMap[c] || 0) + n(r.amount);
  });
  const expenses = Object.entries(expMap).map(([category, amount]) => ({ category, amount }));

  // F. Cash reconciliation
  const expected = n(raw.expected_cash), actual = n(raw.actual_cash);

  // H. KPIs (tank variance / fuel purchased need the report endpoint)
  const revenue  = n(raw.meter_sales) + n(raw.lube_sales);
  const fuelSold = sales.reduce((s, r) => s + (r.product === 'Lubes' ? 0 : n(r.qty)), 0);

  return {
    header: {
      station_name: raw.station_name || '',
      date:         raw.date,
      shift:        'Full Day',
      prepared_by:  raw.closure_info?.closed_by_name || 'Admin',
      closed_at:    raw.closure_info?.closed_at || null,
      generated_on: new Date().toISOString().slice(0, 19).replace('T', ' '),
      is_closed:    raw.is_closed,
    },
    sales_by_product: sales,
    tank_summary:     [],
    collections:      coll,
    total_collection: totalCollection,
    purchase_summary: [],
    expense_summary:  expenses,
    total_expenses:   n(raw.total_expenses),
    cash_reconciliation: { expected_cash: expected, actual_cash: actual, short_excess: +(actual - expected).toFixed(2) },
    variance_summary: [],
    kpis: {
      total_fuel_sold: fuelSold, total_fuel_purchased: 0,
      total_revenue: revenue, total_collection: totalCollection,
      total_expenses: n(raw.total_expenses), net_cash: totalCollection - n(raw.total_expenses),
      total_tank_variance: 0,
    },
    _fallback: true,
  };
}

/* On-screen view of the single-page summary report. */
function SummaryReportModal({ report, station, onClose, onDownload }) {
  const { t: tr } = useTranslation();
  const h  = report.header || {};
  const cr = report.cash_reconciliation || {};
  const k  = report.kpis || {};
  const sales = report.sales_by_product || [];
  const salesQty = sales.reduce((s, r) => s + (r.product === 'Lubes' ? 0 : Number(r.qty || 0)), 0);
  const salesAmt = sales.reduce((s, r) => s + Number(r.amount || 0), 0);
  const purchases = report.purchase_summary || [];
  const purQty = purchases.reduce((s, r) => s + Number(r.qty || 0), 0);
  const purAmt = purchases.reduce((s, r) => s + Number(r.amount || 0), 0);
  const expenses = report.expense_summary || [];

  const ov = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: 20, overflow: 'auto' };
  const sheet = { background: '#fff', borderRadius: 8, padding: 24, width: '210mm', maxWidth: '100%', boxShadow: '0 10px 40px rgba(0,0,0,0.3)' };
  const secTitle = { fontSize: 13, fontWeight: 700, margin: '16px 0 6px', color: 'var(--primary)' };

  return (
    <div style={ov} onClick={onClose}>
      <div style={sheet} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 10 }}>
          <button className="btn btn-primary btn-sm" onClick={onDownload}>{tr('reconciliation.downloadPdf')}</button>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>✕ {tr('common.close')}</button>
        </div>

        <ReportHeader title={tr('reconciliation.dayEndSummaryReport')} header={h} station={station} />
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: -6, marginBottom: 8 }}>
          {tr('reconciliation.generatedOn')} {h.generated_on}
        </div>

        {report._fallback && (
          <div style={{ background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 6, padding: '8px 12px', fontSize: 12, marginBottom: 8 }}>
            ⚠️ {tr('reconciliation.fallbackWarn')} <code>reconciliation.php</code> {tr('reconciliation.fallbackDeploy')} <code>action=report</code> {tr('reconciliation.fallbackEndpoint')}
          </div>
        )}

        <div style={secTitle}>{tr('reconciliation.salesSummaryByProduct')}</div>
        <table className="data-table"><thead><tr><th style={{ textAlign: 'left' }}>{tr('common.product')}</th><th style={{ textAlign: 'right' }}>{tr('reconciliation.qtySold')}</th><th style={{ textAlign: 'right' }}>{tr('common.amount')}</th></tr></thead>
          <tbody>{sales.map((r, i) => <tr key={i}><td>{r.product}</td><td className="right-align">{rQty(r.qty)}</td><td className="right-align">{rMoney(r.amount)}</td></tr>)}
            <tr style={{ fontWeight: 700 }}><td>{tr('common.total')}</td><td className="right-align">{rQty(salesQty)}</td><td className="right-align">{rMoney(salesAmt)}</td></tr></tbody></table>

        <div style={secTitle}>{tr('reconciliation.tankSummary')}</div>
        <table className="data-table"><thead><tr><th style={{ textAlign: 'left' }}>{tr('common.tank')}</th><th>{tr('common.product')}</th><th className="right-align">{tr('common.opening')}</th><th className="right-align">{tr('reconciliation.purchase')}</th><th className="right-align">{tr('reconciliation.sales')}</th><th className="right-align">{tr('reconciliation.testing')}</th><th className="right-align">{tr('common.closing')}</th><th className="right-align">{tr('reconciliation.variance')}</th></tr></thead>
          <tbody>{(report.tank_summary || []).map((t, i) => <tr key={i}><td>{t.tank_no}</td><td>{t.product}</td><td className="right-align">{rQty(t.opening)}</td><td className="right-align">{rQty(t.purchase)}</td><td className="right-align">{rQty(t.sales)}</td><td className="right-align">{rQty(t.testing)}</td><td className="right-align">{rQty(t.closing)}</td><td className="right-align" style={{ color: Number(t.variance) ? 'var(--danger)' : 'inherit' }}>{rVar(t.variance)}</td></tr>)}</tbody></table>

        <div style={secTitle}>{tr('reconciliation.collectionSummary')}</div>
        <table className="data-table"><tbody>
          {Object.entries(report.collections || {}).map(([m, a]) => <tr key={m}><td>{m}</td><td className="right-align">{rMoney(a)}</td></tr>)}
          <tr style={{ fontWeight: 700 }}><td>{tr('reconciliation.totalCollection')}</td><td className="right-align">{rMoney(report.total_collection)}</td></tr></tbody></table>

        <div style={secTitle}>{tr('reconciliation.purchaseSummary')}</div>
        <table className="data-table"><thead><tr><th style={{ textAlign: 'left' }}>{tr('common.product')}</th><th className="right-align">{tr('common.quantity')}</th><th className="right-align">{tr('common.amount')}</th></tr></thead>
          <tbody>{purchases.map((r, i) => <tr key={i}><td>{r.product}</td><td className="right-align">{rQty(r.qty)}</td><td className="right-align">{rMoney(r.amount)}</td></tr>)}
            {purchases.length === 0 && <tr><td colSpan={3} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{tr('reconciliation.noPurchases')}</td></tr>}
            {purchases.length > 0 && <tr style={{ fontWeight: 700 }}><td>{tr('common.total')}</td><td className="right-align">{rQty(purQty)}</td><td className="right-align">{rMoney(purAmt)}</td></tr>}</tbody></table>

        <div style={secTitle}>{tr('reconciliation.expenseSummary')}</div>
        <table className="data-table"><tbody>
          {expenses.map((r, i) => <tr key={i}><td>{r.category}</td><td className="right-align">{rMoney(r.amount)}</td></tr>)}
          <tr style={{ fontWeight: 700 }}><td>{tr('reconciliation.grandTotalExpenses')}</td><td className="right-align">{rMoney(report.total_expenses)}</td></tr></tbody></table>

        <div style={secTitle}>{tr('reconciliation.cashReconciliation')}</div>
        <table className="data-table"><tbody>
          <tr><td>{tr('reconciliation.expectedCashLabel')}</td><td className="right-align">{rMoney(cr.expected_cash)}</td></tr>
          <tr><td>{tr('reconciliation.actualCashLabel')}</td><td className="right-align">{rMoney(cr.actual_cash)}</td></tr>
          <tr style={{ fontWeight: 700, color: Number(cr.short_excess) === 0 ? 'inherit' : (Number(cr.short_excess) < 0 ? 'var(--danger)' : 'var(--secondary)') }}>
            <td>{Number(cr.short_excess) < 0 ? tr('reconciliation.short') : tr('reconciliation.excess')}</td><td className="right-align">{rMoney(Math.abs(Number(cr.short_excess || 0)))}</td></tr></tbody></table>

        <div style={secTitle}>{tr('reconciliation.varianceSummary')}</div>
        <table className="data-table"><thead><tr><th style={{ textAlign: 'left' }}>{tr('common.tank')}</th><th className="right-align">{tr('reconciliation.variance')}</th></tr></thead>
          <tbody>{(report.variance_summary || []).length === 0 ? <tr><td colSpan={2} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{tr('reconciliation.noVarianceRecorded')}</td></tr>
            : report.variance_summary.map((r, i) => <tr key={i}><td>{r.tank_no}</td><td className="right-align" style={{ color: Number(r.variance) ? 'var(--danger)' : 'inherit', fontWeight: 700 }}>{rVar(r.variance)}</td></tr>)}</tbody></table>

        <div style={secTitle}>{tr('reconciliation.keyKpis')}</div>
        <table className="data-table"><tbody>
          <tr><td>{tr('reconciliation.totalFuelSold')}</td><td className="right-align">{rQty(k.total_fuel_sold)} L</td></tr>
          <tr><td>{tr('reconciliation.totalFuelPurchased')}</td><td className="right-align">{rQty(k.total_fuel_purchased)} L</td></tr>
          <tr><td>{tr('reconciliation.totalRevenue')}</td><td className="right-align">{rMoney(k.total_revenue)}</td></tr>
          <tr><td>{tr('reconciliation.totalCollection')}</td><td className="right-align">{rMoney(k.total_collection)}</td></tr>
          <tr><td>{tr('reconciliation.totalExpenses')}</td><td className="right-align">{rMoney(k.total_expenses)}</td></tr>
          <tr><td>{tr('reconciliation.netCash')}</td><td className="right-align">{rMoney(k.net_cash)}</td></tr>
          <tr><td>{tr('reconciliation.totalTankVariance')}</td><td className="right-align">{rVar(k.total_tank_variance)}</td></tr></tbody></table>
      </div>
    </div>
  );
}

/* Builds a print-ready A4 HTML document for the summary report. */
function buildSummaryReportHTML(report, station, tr) {
  const h  = report.header || {};
  const cr = report.cash_reconciliation || {};
  const k  = report.kpis || {};
  const sales = report.sales_by_product || [];
  const salesQty = sales.reduce((s, r) => s + (r.product === 'Lubes' ? 0 : Number(r.qty || 0)), 0);
  const salesAmt = sales.reduce((s, r) => s + Number(r.amount || 0), 0);
  const purchases = report.purchase_summary || [];
  const purQty = purchases.reduce((s, r) => s + Number(r.qty || 0), 0);
  const purAmt = purchases.reduce((s, r) => s + Number(r.amount || 0), 0);
  const rows = (arr) => arr.join('');
  const esc = (s) => String(s ?? '').replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));

  const A = rows(sales.map(r => `<tr><td>${esc(r.product)}</td><td class="r">${rQty(r.qty)}</td><td class="r">${rMoney(r.amount)}</td></tr>`))
    + `<tr class="tot"><td>${esc(tr('common.total'))}</td><td class="r">${rQty(salesQty)}</td><td class="r">${rMoney(salesAmt)}</td></tr>`;
  const B = rows((report.tank_summary || []).map(t => `<tr><td>${esc(t.tank_no)}</td><td>${esc(t.product)}</td><td class="r">${rQty(t.opening)}</td><td class="r">${rQty(t.purchase)}</td><td class="r">${rQty(t.sales)}</td><td class="r">${rQty(t.testing)}</td><td class="r">${rQty(t.closing)}</td><td class="r">${rVar(t.variance)}</td></tr>`));
  const C = rows(Object.entries(report.collections || {}).map(([m, a]) => `<tr><td>${esc(m)}</td><td class="r">${rMoney(a)}</td></tr>`))
    + `<tr class="tot"><td>${esc(tr('reconciliation.totalCollection'))}</td><td class="r">${rMoney(report.total_collection)}</td></tr>`;
  const D = purchases.length
    ? rows(purchases.map(r => `<tr><td>${esc(r.product)}</td><td class="r">${rQty(r.qty)}</td><td class="r">${rMoney(r.amount)}</td></tr>`)) + `<tr class="tot"><td>${esc(tr('common.total'))}</td><td class="r">${rQty(purQty)}</td><td class="r">${rMoney(purAmt)}</td></tr>`
    : `<tr><td colspan="3" style="text-align:center">${esc(tr('reconciliation.noPurchases'))}</td></tr>`;
  const E = rows((report.expense_summary || []).map(r => `<tr><td>${esc(r.category)}</td><td class="r">${rMoney(r.amount)}</td></tr>`))
    + `<tr class="tot"><td>${esc(tr('reconciliation.grandTotalExpenses'))}</td><td class="r">${rMoney(report.total_expenses)}</td></tr>`;
  const G = (report.variance_summary || []).length
    ? rows(report.variance_summary.map(r => `<tr><td>${esc(r.tank_no)}</td><td class="r">${rVar(r.variance)}</td></tr>`))
    : `<tr><td colspan="2" style="text-align:center">${esc(tr('reconciliation.noVarianceRecorded'))}</td></tr>`;

  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(tr('reconciliation.dayEndSummary'))} — ${esc(h.date)}</title>
<style>
  @page { size: A4; margin: 12mm; }
  * { box-sizing: border-box; }
  body { font-family: Arial, sans-serif; color: #1a1a1a; font-size: 11px; }
  .rpt-head { display: flex; justify-content: space-between; gap: 16px; background: #1a6fad; color: #fff; padding: 10px 14px; border-radius: 6px; margin-bottom: 6px; }
  .rpt-left, .rpt-right { font-size: 11px; line-height: 1.5; }
  .rpt-right { text-align: right; }
  .rpt-title { font-size: 15px; font-weight: 800; letter-spacing: 0.5px; margin-bottom: 3px; }
  .rpt-station { font-size: 13px; font-weight: 800; }
  .lbl { opacity: 0.85; }
  .gen { font-size: 9px; color: #888; margin-bottom: 8px; }
  h2 { font-size: 12px; margin: 12px 0 4px; border-bottom: 1px solid #ccc; padding-bottom: 2px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 6px; }
  th, td { border: 1px solid #ddd; padding: 4px 6px; text-align: left; }
  th { background: #f2f6fa; }
  .r { text-align: right; }
  .tot { font-weight: 700; background: #f7f7f7; }
  .grid2 { display: flex; gap: 16px; }
  .grid2 > div { flex: 1; }
</style></head><body>
  ${buildReportHeaderHTML(tr('reconciliation.dayEndSummaryReport'), h, station, tr)}
  <div class="gen">${esc(tr('reconciliation.generatedOn'))} ${esc(h.generated_on)}</div>
  ${report._fallback ? `<div style="background:#fff7e6;border:1px solid #ffd591;border-radius:4px;padding:6px 8px;font-size:10px;margin-bottom:8px">${esc(tr('reconciliation.fallbackNotePrint'))}</div>` : ''}
  <h2>${esc(tr('reconciliation.salesSummaryByProduct'))}</h2>
  <table><thead><tr><th>${esc(tr('common.product'))}</th><th class="r">${esc(tr('reconciliation.qtySold'))}</th><th class="r">${esc(tr('common.amount'))}</th></tr></thead><tbody>${A}</tbody></table>
  <h2>${esc(tr('reconciliation.tankSummary'))}</h2>
  <table><thead><tr><th>${esc(tr('common.tank'))}</th><th>${esc(tr('common.product'))}</th><th class="r">${esc(tr('common.opening'))}</th><th class="r">${esc(tr('reconciliation.purchase'))}</th><th class="r">${esc(tr('reconciliation.sales'))}</th><th class="r">${esc(tr('reconciliation.testing'))}</th><th class="r">${esc(tr('common.closing'))}</th><th class="r">${esc(tr('reconciliation.variance'))}</th></tr></thead><tbody>${B}</tbody></table>
  <div class="grid2">
    <div><h2>${esc(tr('reconciliation.collectionSummary'))}</h2><table><tbody>${C}</tbody></table></div>
    <div><h2>${esc(tr('reconciliation.purchaseSummary'))}</h2><table><thead><tr><th>${esc(tr('common.product'))}</th><th class="r">${esc(tr('common.qty'))}</th><th class="r">${esc(tr('common.amount'))}</th></tr></thead><tbody>${D}</tbody></table></div>
  </div>
  <div class="grid2">
    <div><h2>${esc(tr('reconciliation.expenseSummary'))}</h2><table><tbody>${E}</tbody></table></div>
    <div>
      <h2>${esc(tr('reconciliation.cashReconciliation'))}</h2>
      <table><tbody>
        <tr><td>${esc(tr('reconciliation.expectedCashLabel'))}</td><td class="r">${rMoney(cr.expected_cash)}</td></tr>
        <tr><td>${esc(tr('reconciliation.actualCashLabel'))}</td><td class="r">${rMoney(cr.actual_cash)}</td></tr>
        <tr class="tot"><td>${Number(cr.short_excess) < 0 ? esc(tr('reconciliation.short')) : esc(tr('reconciliation.excess'))}</td><td class="r">${rMoney(Math.abs(Number(cr.short_excess || 0)))}</td></tr>
      </tbody></table>
      <h2>${esc(tr('reconciliation.varianceSummary'))}</h2><table><thead><tr><th>${esc(tr('common.tank'))}</th><th class="r">${esc(tr('reconciliation.variance'))}</th></tr></thead><tbody>${G}</tbody></table>
    </div>
  </div>
  <h2>${esc(tr('reconciliation.keyKpis'))}</h2>
  <table><tbody>
    <tr><td>${esc(tr('reconciliation.totalFuelSold'))}</td><td class="r">${rQty(k.total_fuel_sold)} L</td></tr>
    <tr><td>${esc(tr('reconciliation.totalFuelPurchased'))}</td><td class="r">${rQty(k.total_fuel_purchased)} L</td></tr>
    <tr><td>${esc(tr('reconciliation.totalRevenue'))}</td><td class="r">${rMoney(k.total_revenue)}</td></tr>
    <tr><td>${esc(tr('reconciliation.totalCollection'))}</td><td class="r">${rMoney(k.total_collection)}</td></tr>
    <tr><td>${esc(tr('reconciliation.totalExpenses'))}</td><td class="r">${rMoney(k.total_expenses)}</td></tr>
    <tr><td>${esc(tr('reconciliation.netCash'))}</td><td class="r">${rMoney(k.net_cash)}</td></tr>
    <tr><td>${esc(tr('reconciliation.totalTankVariance'))}</td><td class="r">${rVar(k.total_tank_variance)}</td></tr>
  </tbody></table>
</body></html>`;
}
